import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { Minus, Plus } from "lucide-react";

export function CounterInput({
  title,
  count,
  setCount,
  min = 1,
  max,
  disabled = false,
  className,
}: {
  title?: string;
  count: number;
  setCount: (count: number) => void;
  min?: number;
  max?: number;
  disabled?: boolean;
  className?: string;
}) {
  const handleCountChange = (operator: "add" | "subtract") => {
    if (operator === "subtract" && count <= min) {
      setCount(min);
    } else if (operator === "add" && max !== undefined && count >= max) {
      setCount(max);
    } else {
      setCount(count + (operator === "add" ? 1 : -1));
    }
  };

  const handleInputChange = (value: number) => {
    if (value < min) {
      setCount(min);
    } else if (max !== undefined && value > max) {
      setCount(max);
    } else {
      setCount(value);
    }
  };

  return (
    <div className={className}>
      <div className="gap-2 text-center flex items-center">
        {title && (
          <Label className="text-base md:text-lg font-medium text-foreground whitespace-nowrap">
            {title}
          </Label>
        )}
        <div className="relative inline-flex h-9 w-full items-center overflow-hidden whitespace-nowrap rounded-lg border border-input text-sm shadow-sm shadow-black/5 transition-shadow data-[focus-within]:border-ring data-[disabled]:opacity-50 data-[focus-within]:outline-none data-[focus-within]:ring-[3px] data-[focus-within]:ring-ring/20">
          <Input
            type="number"
            min={min}
            max={max}
            value={count}
            disabled={disabled}
            onChange={(e) => handleInputChange(Number(e.target.value))}
            className="w-full h-[inherit] max-w-[500px] shadow-none bg-secondary px-3 py-2 text-center border-none rounded-none text-foreground focus-visible:ring-0 focus:outline-none"
          />
          <Button
            type="button"
            onClick={() => handleCountChange("subtract")}
            disabled={count <= min || disabled}
            className="-ms-px flex aspect-square h-[inherit] items-center justify-center border-l rounded-none bg-background text-sm text-muted-foreground/80 transition-shadow hover:bg-accent hover:text-foreground disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Minus size={16} strokeWidth={2} aria-hidden="true" />
          </Button>
          <Button
            type="button"
            onClick={() => handleCountChange("add")}
            disabled={(max !== undefined && count >= max) || disabled}
            className="-me-px flex aspect-square h-[inherit] items-center justify-center border-l rounded-none bg-background text-sm text-muted-foreground/80 transition-shadow hover:bg-accent hover:text-foreground disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Plus size={16} strokeWidth={2} aria-hidden="true" />
          </Button>
        </div>
      </div>
    </div>
  );
}
